import { PhoneIcon } from 'lucide-react';
import type { ConditionalVisibility } from 'korint-sdk/react';
import { IdentityProviders, type UniqAppConfig } from 'korint-sdk/react';
import { CreateRetailBrokerageFirmModule } from '@korint/folio';
import { PolicyDashboardEvents } from '../custom-pages/PolicyDashboardEvents';
import { ContactsUtiles } from '../custom-pages/ContactsUtiles';

const showWhenCompanyBeingCreated = [
  {
    fieldName: 'isCompanyBeingCreated',
    condition: 'equals',
    value: true,
  },
] as const satisfies ConditionalVisibility[];

const showWhenSiretSearch = [
  {
    fieldName: 'isCompanyBeingCreated',
    condition: 'notEquals',
    value: true,
  },
] as const satisfies ConditionalVisibility[];

export const uniqAppConfig = {
  title: 'Tutassur',
  favicon: '/tutassur-logo.png',
  logoPath: '/tutassur-logo.png',
  colors: {
    primary: '#684BDF',
    primaryForeground: 'hsl(210 40% 98%)',
    secondary: '#684BDF',
    secondaryForeground: 'hsl(11 0% 100%)',
  },
  parentBrokerageFirmId: 'brokerage_firm_9hYLyYKM3GTVqMECjg3NH',
  policyholderDashboard: {
    pages: [
      { type: 'home' },
      { type: 'information' },
      { type: 'documents' },
      { type: 'payment-methods' },
      { type: 'invoices' },
    ],
  },
  intermediaryDashboard: {
    pages: [
      { type: 'ongoing-new-business', forTypes: ['WHOLESALE', 'RETAIL'] },
      { type: 'derogations', forTypes: ['WHOLESALE', 'RETAIL'] },
      { type: 'portfolio', forTypes: ['WHOLESALE', 'RETAIL'] },
      { type: 'archived-new-business', forTypes: ['WHOLESALE', 'RETAIL'] },
      { type: 'my-brokerage-firm', forTypes: ['WHOLESALE', 'RETAIL'] },
      { type: 'reports', forTypes: ['WHOLESALE', 'RETAIL'] },
      { type: 'intermediary-user-list', forTypes: ['WHOLESALE', 'RETAIL'] },
      { type: 'accounting', forTypes: ['WHOLESALE', 'RETAIL'] },
      { type: 'brokerage-firm-list', forTypes: ['WHOLESALE'] },
      { type: 'products', forTypes: ['WHOLESALE', 'RETAIL'] },
      { type: 'analytics', forTypes: ['WHOLESALE'] },
      {
        type: 'custom',
        path: '/contacts-utiles',
        name: 'sidebar.intermediary.contactsUtiles',
        icon: <PhoneIcon className="size-4" />,
        component: <ContactsUtiles />,
        forTypes: ['RETAIL'],
      },
      { type: 'conversations' },
      { type: 'general-inbox' },
    ],
    otherPages: [{ type: 'intermediary-user-form' }],
  },
  policyDashboard: {
    pages: [
      'home',
      'informations',
      'payment-methods',
      'documents',
      'invoices',
      { type: 'custom', path: '/events', component: <PolicyDashboardEvents /> },
      'notifications',
      'compliance',
    ],
    canModifySignedPolicy: ['WHOLESALE', 'RETAIL'],
  },
  intermediaryFunnelSteps: {
    steps: [
      {
        type: 'module',
        path: 'creation',
        module: <CreateRetailBrokerageFirmModule />,
        hidden: true,
      },
      {
        path: 'documents-upload',
        type: 'documentUploadFunnel' as const,
        context: 'brokerageFirm' as const,
      },
      {
        type: 'intermediaryFunnel',
        path: 'information',
        fields: [
          {
            name: 'isCompanyBeingCreated',
            required: false,
            type: 'checkbox',
            ignoreInPayload: true,
          },
          {
            name: 'siret',
            required: true,
            type: 'company',
            apiDataAccessor: 'companyInfo',
            formDataAccessor: 'siret.siret',
            isCustomField: true,
            showWhen: showWhenSiretSearch,
          },
          {
            name: 'companyName',
            required: true,
            type: 'text',
            apiDataAccessor: (firm: Record<string, unknown>) =>
              (firm.companyInfo as { name?: string } | undefined)?.name,
            ignoreInPayload: true,
            showWhen: showWhenCompanyBeingCreated,
          },
          {
            name: 'companySiret',
            required: true,
            type: 'text',
            minLength: 14,
            maxLength: 14,
            apiDataAccessor: (firm: Record<string, unknown>) =>
              (firm.companyInfo as { siret?: string } | undefined)?.siret,
            ignoreInPayload: true,
            showWhen: showWhenCompanyBeingCreated,
          },
          {
            name: 'companyAddress',
            required: true,
            type: 'text',
            apiDataAccessor: (firm: Record<string, unknown>) =>
              (firm.companyInfo as { location?: { address?: string } } | undefined)?.location
                ?.address,
            ignoreInPayload: true,
            showWhen: showWhenCompanyBeingCreated,
          },
          {
            name: 'companyPostalCode',
            required: true,
            type: 'text',
            apiDataAccessor: (firm: Record<string, unknown>) =>
              (firm.companyInfo as { location?: { postCode?: string } } | undefined)?.location
                ?.postCode,
            ignoreInPayload: true,
            showWhen: showWhenCompanyBeingCreated,
          },
          {
            name: 'companyCity',
            required: true,
            type: 'text',
            apiDataAccessor: (firm: Record<string, unknown>) =>
              (firm.companyInfo as { location?: { city?: string } } | undefined)?.location?.city,
            ignoreInPayload: true,
            showWhen: showWhenCompanyBeingCreated,
          },
          {
            name: 'companyInfoPatch',
            type: 'computed',
            required: false,
            hidden: true,
            dependsOn: [
              'isCompanyBeingCreated',
              'companyName',
              'companySiret',
              'companyAddress',
              'companyPostalCode',
              'companyCity',
            ],
            formDataAccessor: (form: Record<string, unknown>) => {
              if (form.isCompanyBeingCreated !== true) return {};
              const siret = typeof form.companySiret === 'string' ? form.companySiret : undefined;
              return {
                companyInfo: {
                  name: form.companyName,
                  siret,
                  siren: siret ? siret.slice(0, 9) : undefined,
                  location: {
                    address: form.companyAddress,
                    postCode: form.companyPostalCode,
                    city: form.companyCity,
                    country: 'France',
                  },
                },
              };
            },
          },
          {
            name: 'orias',
            required: true,
            type: 'text',
            isCustomField: true,
            minLength: 8,
            maxLength: 8,
          },
          {
            name: 'brokerNumberOfEmployees',
            required: true,
            type: 'text',
            isCustomField: true,
          },
          {
            name: 'brokerDirectorLastName',
            required: true,
            type: 'text',
            isCustomField: true,
          },
          {
            name: 'brokerDirectorFirstName',
            required: true,
            type: 'text',
            isCustomField: true,
          },
          {
            name: 'brokerDirectorEmail',
            required: true,
            type: 'email',
            isCustomField: true,
          },
          {
            name: 'brokerDirectorPhoneNumber',
            required: true,
            type: 'phoneNumber',
            countries: ['FR'],
            isCustomField: true,
          },
          {
            name: 'brokerDirectorBirthDate',
            required: true,
            type: 'date',
            isCustomField: true,
          },
        ],
      },
      {
        type: 'intermediaryFunnel',
        path: 'contact-information',
        fields: [
          {
            name: 'brokerContactLastName',
            required: true,
            type: 'text',
            isCustomField: true,
          },
          {
            name: 'brokerContactFirstName',
            required: true,
            type: 'text',
            isCustomField: true,
          },
          {
            name: 'brokerContactEmail',
            required: true,
            type: 'email',
            isCustomField: true,
          },
          {
            name: 'brokerContactPhoneNumber',
            required: true,
            type: 'phoneNumber',
            countries: ['FR'],
            isCustomField: true,
          },
        ],
      },
      {
        type: 'intermediaryFunnel',
        path: 'payment-method',
        fields: [
          {
            name: 'iban',
            type: 'text',
            required: true,
            isCustomField: true,
          },
          {
            name: 'bic',
            type: 'text',
            required: true,
            isCustomField: true,
          },
        ],
      },
      {
        type: 'brokerageFirmUploadDocumentsFunnelValidated',
        path: 'documents',
      },
      {
        path: 'product',
        type: 'distributionRequestFunnel',
      },
      {
        type: 'intermediaryFunnelEnd',
        path: 'end',
      },
    ],
  },
  intermediaryDetailsPages: {
    intermediaryInformations: [
      {
        titleKey: 'companyInformation',
        fields: [
          {
            accessorKey: 'companyInfo.siret',
            key: 'siret',
            type: 'text',
            allowEdition: true,
            required: true,
            minLength: 14,
            maxLength: 14,
          },
          {
            accessorKey: 'companyInfo.name',
            key: 'name',
            type: 'text',
            allowEdition: true,
            required: true,
            formDataAccessor: value => ({ companyInfo: { name: String(value) } }),
          },
          {
            kind: 'group',
            key: 'address',
            fields: [
              {
                key: 'address',
                accessorKey: 'companyInfo.location.address',
                type: 'text',
                required: true,
              },
              {
                key: 'postCode',
                accessorKey: 'companyInfo.location.postCode',
                type: 'postcode',
                countries: ['FR'],
                required: true,
              },
              {
                key: 'city',
                accessorKey: 'companyInfo.location.city',
                type: 'text',
                required: true,
              },
            ],
            formDataAccessor: values => ({
              companyInfo: {
                location: {
                  address: values.address === undefined ? undefined : String(values.address),
                  postCode: values.postCode === undefined ? undefined : String(values.postCode),
                  city: values.city === undefined ? undefined : String(values.city),
                },
              },
            }),
          },
          { accessorKey: 'companyInfo.siren', key: 'siren', type: 'text' },
          { accessorKey: 'companyInfo.nafCode', key: 'nafCode', type: 'text' },
          {
            accessorKey: 'customFields.brokerNumberOfEmployees',
            key: 'numberOfEmployees',
            type: 'number',
          },
        ],
      },
      {
        titleKey: 'bankingInformation',
        fields: [
          {
            key: 'iban',
            accessorKey: 'customFields.iban',
            type: 'text',
            allowEdition: true,
            required: true,
          },
          {
            key: 'bic',
            accessorKey: 'customFields.bic',
            type: 'text',
            allowEdition: true,
            required: true,
          },
        ],
      },
      {
        titleKey: 'directorInformation',
        fields: [
          {
            accessorKey: 'customFields.brokerDirectorLastName',
            key: 'brokerDirectorLastName',
            type: 'text',
            allowEdition: true,
            required: true,
          },
          {
            accessorKey: 'customFields.brokerDirectorFirstName',
            key: 'brokerDirectorFirstName',
            type: 'text',
            allowEdition: true,
            required: true,
          },
          {
            accessorKey: 'customFields.brokerDirectorEmail',
            key: 'brokerDirectorEmail',
            type: 'email',
            allowEdition: true,
            required: true,
          },
          {
            accessorKey: 'customFields.brokerDirectorPhoneNumber',
            key: 'brokerDirectorPhoneNumber',
            type: 'tel',
            allowEdition: true,
            required: true,
          },
        ],
      },
      {
        titleKey: 'contactInformation',
        fields: [
          {
            accessorKey: 'customFields.brokerContactLastName',
            key: 'brokerContactLastName',
            type: 'text',
            allowEdition: true,
            required: true,
          },
          {
            accessorKey: 'customFields.brokerContactFirstName',
            key: 'brokerContactFirstName',
            type: 'text',
            allowEdition: true,
            required: true,
          },
          {
            accessorKey: 'customFields.brokerContactEmail',
            key: 'brokerContactEmail',
            type: 'email',
            allowEdition: true,
            required: true,
          },
          {
            accessorKey: 'customFields.brokerContactPhoneNumber',
            key: 'brokerContactPhoneNumber',
            type: 'tel',
            allowEdition: true,
            required: true,
          },
        ],
      },
      {
        titleKey: 'comment',
        fields: [
          {
            accessorKey: 'customFields.brokerComment',
            key: 'brokerComment',
            type: 'text',
            allowEdition: true,
          },
        ],
      },
    ],
  },
  login: {
    showLastLoginHint: true,
    providers: [IdentityProviders.GOOGLE, IdentityProviders.APPLE],
  },
} as const satisfies UniqAppConfig;
